import type { ImpersonationInfo } from '@platform/contracts';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Label,
  Textarea,
} from '@platform/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Eye, LogOut } from 'lucide-react';
import { useEffect, useId, useState } from 'react';
import { toast } from 'sonner';

import { api, errorMessage } from '../lib/api';
import { meQuery } from '../lib/queries';
import { FormError } from './form';

/** Minutos que faltan para que venza la impersonación. */
function useMinutesLeft(expiresAt: string): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => {
      setNow(Date.now());
    }, 30_000);
    return () => {
      clearInterval(t);
    };
  }, []);
  return Math.max(0, Math.ceil((new Date(expiresAt).getTime() - now) / 60_000));
}

/**
 * Banner permanente mientras el superadmin "ve como" otra persona: sólo lectura, con el tiempo que
 * queda y un botón para salir. Siempre visible arriba de todo (no se puede cerrar).
 */
export function ImpersonationBanner({ info }: { info: ImpersonationInfo }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const left = useMinutesLeft(info.expiresAt);
  const stop = useMutation({
    mutationFn: () => api('/api/impersonation/stop', { method: 'POST' }),
    onSuccess: async () => {
      qc.clear();
      toast.success('Volviste a tu sesión');
      await navigate({ to: '/superadmin/usuarios' });
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  return (
    <div
      role="status"
      className="sticky top-0 z-30 flex flex-wrap items-center gap-3 bg-warning px-4 py-2 text-sm text-warning-foreground"
    >
      <Eye className="size-4" aria-hidden />
      <span>
        Estás viendo la consola como <strong>{info.targetName}</strong> ({info.targetEmail}) · sólo
        lectura · vence en {left} min
      </span>
      <Button
        size="sm"
        variant="secondary"
        className="ml-auto"
        onClick={() => {
          stop.mutate();
        }}
        disabled={stop.isPending}
      >
        <LogOut /> Salir de "ver como"
      </Button>
    </div>
  );
}

/** Botón del panel superadmin: pide el motivo (queda en la auditoría) y entra como esa persona. */
export function ImpersonateButton({
  user,
  open: controlled,
  onOpenChange,
}: {
  user: { id: string; email: string; name: string };
  /** Controlado desde afuera (p. ej. un menú de acciones): no dibuja su propio botón. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [own, setOwn] = useState(false);
  const open = controlled ?? own;
  const setOpen = onOpenChange ?? setOwn;
  const [reason, setReason] = useState('');
  const reasonId = useId();
  const start = useMutation({
    mutationFn: () =>
      api<ImpersonationInfo>('/api/admin/impersonation', {
        method: 'POST',
        body: { userId: user.id, reason },
      }),
    onSuccess: async () => {
      qc.clear();
      setOpen(false);
      await qc.query(meQuery);
      await navigate({ to: '/overview', search: { scope: 'all' } });
    },
  });
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {controlled === undefined ? (
        <DialogTrigger asChild>
          <Button variant="ghost" size="sm">
            <Eye /> Ver como
          </Button>
        </DialogTrigger>
      ) : null}
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Ver la consola como {user.name}</DialogTitle>
          <DialogDescription>
            Sólo lectura, 30 minutos. Queda registrado en la auditoría contigo como actor.
          </DialogDescription>
        </DialogHeader>
        <form
          className="flex flex-col gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            start.mutate();
          }}
        >
          <Label htmlFor={reasonId}>Motivo</Label>
          <Textarea
            id={reasonId}
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
            }}
            placeholder="Ej.: el cliente dice que no ve sus escalaciones"
            minLength={10}
            maxLength={500}
            required
          />
          <FormError message={start.error ? errorMessage(start.error) : null} />
          <DialogFooter>
            <Button type="submit" disabled={start.isPending || reason.trim().length < 10}>
              <Eye /> Entrar como {user.email}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
